import { useEffect, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';
import { useReducedMotion } from '@/lib/motion/useReducedMotion';
import { onRevealBelt } from '@/lib/motion/revealBelt';
import { onRevealSweep } from '@/lib/motion/revealSweep';
import { cx } from '@/lib/cx';
import styles from './Reveal.module.css';

export interface RevealProps {
  children: ReactNode;
  /** Retardo en segundos. Para escalonar hermanos usa `RevealGroup`. */
  delay?: number;
  /** Desplazamiento inicial en px. Por defecto 20 (doc 04). */
  shift?: number;
  as?: 'div' | 'li' | 'section' | 'article' | 'span';
  className?: string;
}

/** Estilo con las dos variables que parametrizan la entrada. */
interface RevealStyle extends CSSProperties {
  '--reveal-delay': string;
  '--reveal-shift': string;
}

/**
 * Entrada fade-up de 20px la PRIMERA vez que el bloque entra en pantalla
 * (doc 04). Una vez mostrado, se queda: nunca se revierte al salir.
 *
 * TRES GARANTIAS, las tres nacidas de un fallo real (20260725):
 *
 *  1. **Lo que ya esta en pantalla al cargar se muestra sin esperar scroll.**
 *     Se mide el rectangulo en el primer efecto: si toca el viewport, entra ya.
 *     Antes dependia de `whileInView` con `amount: 0.2`, asi que un hero mas
 *     alto que la pantalla no llegaba nunca al 20% visible y se quedaba en
 *     blanco con el `h1` presente en el DOM (`/cancun`, hub, Bogota, Miami).
 *  2. **Una sola vez.** El observador se desconecta al primer cruce.
 *  3. **La animacion es una MEJORA, no la condicion para que se vea.** Se hace
 *     con clases y una transicion CSS, no con WAAPI: una captura con
 *     `animations: 'disabled'` — que cancela las animaciones de Framer Motion —
 *     deja el contenido VISIBLE, solo que sin transicion. Y si nadie hace
 *     scroll (captura `fullPage`, impresion), el cinturon de `revealBelt` lo
 *     revela todo a los 1200 ms.
 *  4. **Ninguna velocidad de scroll deja un bloque invisible** (20260725,
 *     medido por ECHO: un barrido de 0,8 x viewport cada 60 ms dejaba 8 bloques
 *     en `opacity: 0`). Un salto grande puede pasar por encima de un bloque
 *     entero entre dos frames y el observador no lo ve cruzar; por eso, ademas
 *     del observador, `revealSweep` vuelve a medir lo que sigue pendiente en
 *     cada frame de scroll y al pararse. Lo que el scroll ya ha pasado, se ve.
 *
 * CONTRATO DE MOVIMIENTO REDUCIDO: con `prefers-reduced-motion: reduce` esto
 * renderiza el contenido VISIBLE Y QUIETO desde el primer frame. Jamas deja
 * algo en `opacity: 0` esperando un trigger que no va a llegar — esconder
 * contenido "por accesibilidad" seria el peor de los fallos.
 */
export function Reveal({ children, delay = 0, shift = 20, as = 'div', className }: RevealProps) {
  const reduced = useReducedMotion();
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);
  const Tag = as as ElementType;

  useEffect(() => {
    if (!node || shown || reduced) return;

    // 1 · Lo que YA se ve al montar entra sin intermediarios, y contra el
    //     viewport ENTERO (sin el margen negativo del observador): un bloque
    //     que el usuario ya tiene delante no puede quedarse invisible. Va en
    //     un frame aparte, despues del primer pintado, para que la transicion
    //     de verdad se vea y para no encadenar renders dentro del efecto.
    const viewportHeight = () => window.innerHeight || document.documentElement.clientHeight;

    const frame = requestAnimationFrame(() => {
      const rect = node.getBoundingClientRect();
      if (rect.top < viewportHeight() && rect.bottom > 0) setShown(true);
    });

    // 2 · El resto, al primer pixel que cruza. `threshold: 0` a proposito: con
    //     un porcentaje del elemento, uno mas alto que la pantalla no dispara.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(node);

    // 3 · Cinturon: si no hay humano que haga scroll, se revela igualmente.
    const unsubscribeBelt = onRevealBelt(() => setShown(true));

    // 4 · Barrido: si SI lo hay y va rapido, el observador puede no llegar a
    //     ver el cruce. Se comprueba `rect.top < alto del viewport` — o sea,
    //     "el borde inferior de la pantalla ya ha alcanzado este bloque" — y no
    //     "esta visible ahora mismo": un bloque por el que el scroll ha pasado
    //     de largo tambien tiene que quedar revelado, o al volver a subir
    //     aparece en blanco.
    const unsubscribeSweep = onRevealSweep(() => {
      if (node.getBoundingClientRect().top < viewportHeight()) setShown(true);
    });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      unsubscribeBelt();
      unsubscribeSweep();
    };
  }, [node, shown, reduced]);

  if (reduced) {
    return <Tag className={cx(styles.reveal, className)}>{children}</Tag>;
  }

  const style: RevealStyle = {
    '--reveal-delay': `${delay}s`,
    '--reveal-shift': `${shift}px`,
  };

  return (
    <Tag
      ref={setNode}
      className={cx(styles.reveal, shown ? styles.shown : styles.pending, className)}
      style={style}
    >
      {children}
    </Tag>
  );
}
